import { useEffect, useRef, useCallback, forwardRef } from "react";
import OpenSeadragon from "openseadragon";

interface OpenSeadragonViewerProps {
  hiddenTileIndexes: number[];
  gridSize: number;
  tokenAspectRatio?: number;
  onTileClick?: (tileIndex: number) => void;
  tileSource: string;
  selectedTokenIndex?: number | null;
}

// Add the canvas overlay type
interface CanvasOverlay {
  canvas: () => HTMLCanvasElement;
  context2d: () => CanvasRenderingContext2D;
  clear: () => void;
  resize: () => void;
  onRedraw: () => void;
}

// Add the plugin type to OpenSeadragon
declare module "openseadragon" {
  interface Viewer {
    canvasOverlay: (options?: {
      onRedraw: () => void;
      clearBeforeRedraw?: boolean;
    }) => CanvasOverlay;
    _canvasOverlayInfo?: CanvasOverlay;
  }
}

// Define the overlay plugin
// Modified from https://github.com/altert/OpenSeadragonCanvasOverlay/blob/5a9da37d7e707966e695d10e347319b34df2667d/openseadragon-canvas-overlay.js
const initCanvasOverlay = (OpenSeadragon: typeof import("openseadragon")) => {
  class Overlay {
    private _viewer: OpenSeadragon.Viewer;
    private _containerWidth: number;
    private _containerHeight: number;
    private _canvasdiv: HTMLDivElement;
    private _canvas: HTMLCanvasElement;
    private _viewportOrigin: any = null;
    private _viewportWidth: number = 0;
    private _viewportHeight: number = 0;
    private imgWidth: number = 0;
    private imgHeight: number = 0;
    private imgAspectRatio: number = 1;
    public onRedraw: () => void;
    public clearBeforeRedraw: boolean;

    constructor(
      viewer: OpenSeadragon.Viewer,
      options?: { onRedraw?: () => void; clearBeforeRedraw?: boolean }
    ) {
      this._viewer = viewer;
      this._containerWidth = 0;
      this._containerHeight = 0;

      // Create canvas container
      this._canvasdiv = document.createElement("div");
      Object.assign(this._canvasdiv.style, {
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
      });
      this._viewer.canvas.appendChild(this._canvasdiv);

      // Create canvas
      this._canvas = document.createElement("canvas");
      this._canvasdiv.appendChild(this._canvas);

      // Set options
      this.onRedraw = options?.onRedraw || (() => {});
      this.clearBeforeRedraw = options?.clearBeforeRedraw ?? true;

      // Add event handlers
      this._viewer.addHandler("update-viewport", () => {
        this.resize();
        this._updateCanvas();
      });

      this._viewer.addHandler("open", () => {
        this.resize();
        this._updateCanvas();
      });
    }

    canvas() {
      return this._canvas;
    }

    context2d() {
      const context = this._canvas.getContext("2d");
      if (!context) {
        throw new Error("Failed to get 2D context");
      }
      return context;
    }

    clear() {
      const context = this._canvas.getContext("2d");
      if (context) {
        context.clearRect(0, 0, this._containerWidth, this._containerHeight);
      }
    }

    resize() {
      // Update container width
      if (this._containerWidth !== this._viewer.container.clientWidth) {
        this._containerWidth = this._viewer.container.clientWidth;
        this._canvasdiv.setAttribute("width", String(this._containerWidth));
        this._canvas.setAttribute("width", String(this._containerWidth));
      }

      // Update container height
      if (this._containerHeight !== this._viewer.container.clientHeight) {
        this._containerHeight = this._viewer.container.clientHeight;
        this._canvasdiv.setAttribute("height", String(this._containerHeight));
        this._canvas.setAttribute("height", String(this._containerHeight));
      }

      // Update viewport information
      this._viewportOrigin = new OpenSeadragon.Point(0, 0);
      const boundsRect = this._viewer.viewport.getBoundsNoRotate(true);

      const image1 = this._viewer.world.getItemAt(0);
      if (image1?.source?.dimensions) {
        this.imgWidth = image1.source.dimensions.x;
        this.imgHeight = image1.source.dimensions.y;
        this.imgAspectRatio = this.imgWidth / this.imgHeight;

        this._viewportOrigin.x = boundsRect.x;
        this._viewportOrigin.y = boundsRect.y * this.imgAspectRatio;

        this._viewportWidth = boundsRect.width;
        this._viewportHeight = boundsRect.height * this.imgAspectRatio;
      }
    }

    private _updateCanvas() {
      const context = this._canvas.getContext("2d");
      if (!context) return;

      const viewportZoom = this._viewer.viewport.getZoom(true);
      const scale = viewportZoom * this._containerWidth;
      const centerX = this._canvas.width / 2;
      const centerY = this._canvas.height / 2;
      const degrees = this._viewer.viewport.getRotation();
      const DEG2RAD = Math.PI / 180;

      const x =
        ((this._viewportOrigin.x / this.imgWidth - this._viewportOrigin.x) /
          this._viewportWidth) *
        this._containerWidth;
      const y =
        ((this._viewportOrigin.y / this.imgHeight - this._viewportOrigin.y) /
          this._viewportHeight) *
        this._containerHeight;

      if (this.clearBeforeRedraw) {
        this.clear();
      }

      // Apply transformations
      context.translate(centerX, centerY);
      context.rotate(degrees * DEG2RAD);
      context.translate(-centerX, -centerY);

      context.translate(x, y);
      context.scale(scale, scale);
      this.onRedraw();
      context.setTransform(1, 0, 0, 1, 0, 0);
    }
  }

  // Add the canvasOverlay method to the OpenSeadragon Viewer prototype
  OpenSeadragon.Viewer.prototype.canvasOverlay = function (options) {
    if (this._canvasOverlayInfo) {
      return this._canvasOverlayInfo;
    }

    const overlay = new Overlay(this, options);
    if (!overlay) {
      throw new Error("Failed to create canvas overlay");
    }

    this._canvasOverlayInfo = overlay;
    return overlay;
  };
};

function OpenSeadragonViewer({
  hiddenTileIndexes,
  gridSize,
  tokenAspectRatio = 1,
  onTileClick,
  tileSource,
  selectedTokenIndex,
}: OpenSeadragonViewerProps) {
  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);

  // Initialize viewer
  useEffect(() => {
    if (!viewerRef.current) {
      initCanvasOverlay(OpenSeadragon);

      const viewer = OpenSeadragon({
        id: "openseadragon-viewer",
        tileSources: tileSource,
        showNavigationControl: false,
        homeFillsViewer: true,
        minZoomLevel: 1,
        // defaultZoomLevel: 1,
        constrainDuringPan: true,
        visibilityRatio: 1,
        gestureSettingsMouse: {
          clickToZoom: false,
          dblClickToZoom: true,
        },
      });

      viewerRef.current = viewer;
    }

    return () => {
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [tileSource]);

  // Add tile click handler to viewer
  useEffect(() => {
    if (!viewerRef.current) return;

    const handler = (event: OpenSeadragon.CanvasClickEvent) => {
      if (!event.quick || !viewerRef.current) return;

      const point = event.position;
      const bounds = viewerRef.current.world.getItemAt(0)?.getBounds();

      if (!bounds) return;

      const gridPosition = getGridPosition(
        point,
        bounds,
        gridSize,
        tokenAspectRatio,
        viewerRef.current
      );

      if (gridPosition !== null) {
        onTileClick?.(gridPosition.index);
      }
    };

    viewerRef.current.addHandler("canvas-click", handler);

    return () => {
      viewerRef.current?.removeHandler("canvas-click", handler);
    };
  }, [onTileClick, tokenAspectRatio, gridSize]);

  // De-emphasize hidden tokens, effectively highlighting all other tokens
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const overlay = viewer.canvasOverlay();
    overlay.onRedraw = () => {
      const context = overlay.context2d();

      const tileWidth = 1 / gridSize;
      const tileHeight = tileWidth / tokenAspectRatio;

      const lineWidth = 0.005 / Math.sqrt(gridSize);

      context.fillStyle = "rgba(0, 0, 0, 0.8)";
      context.lineWidth = lineWidth;

      for (const tileIndex of hiddenTileIndexes) {
        const row = Math.floor(tileIndex / gridSize);
        const col = tileIndex % gridSize;
        const x = col * tileWidth;
        const y = row * tileHeight;

        context.fillRect(x, y, tileWidth, tileHeight);
      }
    };

    viewer.forceRedraw();
  }, [hiddenTileIndexes, gridSize, tokenAspectRatio]);

  // Add overlay for selected token
  useEffect(() => {
    const viewer = viewerRef.current;
    if (
      !viewer ||
      selectedTokenIndex === undefined ||
      selectedTokenIndex === null
    ) {
      return;
    }

    const row = Math.floor(selectedTokenIndex / gridSize);
    const col = selectedTokenIndex % gridSize;

    // Create overlay element
    const overlay = createOverlayElement(
      "border-2 border-white pointer-events-none mix-blend-difference"
    );

    // Calculate the rect in normalized coordinates (0-1)
    const rect = createOverlayRect(row, col, gridSize, tokenAspectRatio);

    // Add the overlay
    viewer.addOverlay({
      element: overlay,
      location: rect,
      placement: OpenSeadragon.Placement.TOP_LEFT,
      checkResize: false,
      id: "selection-overlay", // Specify the ID
    });

    return () => {
      viewer?.removeOverlay(overlay);
    };
  }, [selectedTokenIndex, gridSize, tokenAspectRatio]);

  // Add hover overlay
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    let currentHoverIndex: number | null = null;

    const tracker = new OpenSeadragon.MouseTracker({
      element: viewer.canvas,
      moveHandler: (event) => {
        if (!viewer.viewport) return;

        const bounds = viewer.world.getItemAt(0)?.getBounds();
        if (!bounds) return;

        const webPoint = (
          event as OpenSeadragon.MouseTrackerEvent<Event> & {
            position: OpenSeadragon.Point;
          }
        ).position;

        const gridPosition = getGridPosition(
          webPoint,
          bounds,
          gridSize,
          tokenAspectRatio,
          viewer
        );

        if (!gridPosition) {
          // Remove overlay when outside grid
          currentHoverIndex = null;
          viewer.removeOverlay("hover-overlay");
          return;
        }

        const { col, row, index: hoverIndex } = gridPosition;
        // Only update overlay if hovering over a different tile
        if (hoverIndex !== currentHoverIndex) {
          currentHoverIndex = hoverIndex;

          const hoverOverlay = createOverlayElement(
            "border-2 pointer-events-none border-white/20 mix-blend-difference",
            "hover-overlay"
          );
          const rect = createOverlayRect(row, col, gridSize, tokenAspectRatio);

          // Remove old overlay and add new one
          viewer.removeOverlay("hover-overlay");
          viewer.addOverlay({
            element: hoverOverlay,
            location: rect,
            placement: OpenSeadragon.Placement.TOP_LEFT,
            checkResize: false,
            id: "hover-overlay",
          });
        }
      },
      leaveHandler: () => {
        currentHoverIndex = null;
        viewer.removeOverlay("hover-overlay");
      },
    });

    return () => {
      tracker.destroy();
      viewer.removeOverlay("hover-overlay");
    };
  }, [gridSize, tokenAspectRatio]);

  return <div id="openseadragon-viewer" className="w-full h-full" />;
}

// Utility functions for overlay positioning
function getGridPosition(
  webPoint: OpenSeadragon.Point,
  bounds: OpenSeadragon.Rect,
  gridSize: number,
  tokenAspectRatio: number,
  viewer: OpenSeadragon.Viewer
) {
  const point = viewer.viewport.pointFromPixel(webPoint);

  // Get position within the image bounds
  const x = point.x - bounds.x;
  const y = point.y - bounds.y;

  // Convert to grid coordinates
  const tileWidth = bounds.width / gridSize;
  const tileHeight = tileWidth / tokenAspectRatio;
  const col = Math.floor(x / tileWidth);
  const row = Math.floor(y / tileHeight);

  // Check if within grid bounds
  if (col >= 0 && col < gridSize && row >= 0 && row < gridSize) {
    return { row, col, index: row * gridSize + col };
  }
  return null;
}

function createOverlayRect(
  row: number,
  col: number,
  gridSize: number,
  tokenAspectRatio: number
) {
  return new OpenSeadragon.Rect(
    col / gridSize, // x
    row / (gridSize * tokenAspectRatio), // y
    1 / gridSize, // width
    1 / (gridSize * tokenAspectRatio) // height
  );
}

function createOverlayElement(className: string, id?: string) {
  const overlay = document.createElement("div");
  overlay.className = `border-2 pointer-events-none ${className}`;
  if (id) {
    overlay.id = id;
  }
  return overlay;
}

export default OpenSeadragonViewer;
